import React, { useState, useEffect, useCallback } from 'react';
import { Client, ClientStatus, UserRole, TaskStatus, OnboardingTasks, FileAttachment, User, SatisfactionLevel, RenewalPrediction } from '../types';
import Modal from './ui/Modal';
import { SparklesIcon, BrainCircuitIcon, XIcon, SmileIcon, MehIcon, FrownIcon } from './ui/icons';
import { generateRemarksSuggestion, generateSatisfactionAnalysis } from '../services/geminiService';
import DatePicker from 'react-datepicker';
import { usePermission } from '../services/PermissionContext';
import {
  deriveMonthlyFromYearly,
  deriveYearlyFromMonthly,
  getMonthlyExpectedToDate,
  getYearlyExpectedToDate,
  getYearlyBillingStart,
  getMonthlyBillingStart,
  todayDateInputValue,
} from '../utils/clientBilling';

interface ClientDetailsModalProps {
  client: Client;
  isOpen: boolean;
  onClose: () => void;
  onSave: (client: Client) => void;
  currentUserRoles: string[];
  onTaskStatusUpdateRequest: (clientId: string, taskKey: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>, newStatus: TaskStatus) => void;
  currentUsername: string | null;
  error?: string | null;
}

const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({ client, isOpen, onClose, onSave, currentUserRoles, onTaskStatusUpdateRequest, currentUsername, error }) => {
  const [formData, setFormData] = useState<Client>(client);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [newAttachmentName, setNewAttachmentName] = useState('');
  // Remove Invoice from newAttachmentType state and dropdown, only allow 'Contract'
  const [newAttachmentType] = useState<'Contract'>('Contract');
  const [newAttachmentFile, setNewAttachmentFile] = useState<File | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [accountingCompanies, setAccountingCompanies] = useState<
    { code: string; name: string; displayName?: string; accountingMethod?: string }[]
  >([]);

  // Debug: Log localError changes
  useEffect(() => {
    console.log('localError changed to:', localError);
  }, [localError]);

  const isNewClient = !client.id;

  const isSalesAgent = currentUserRoles.includes('Sales Agent');
  const isOperator = currentUserRoles.includes('Operator');
  const isManager = currentUserRoles.includes('Manager');
  const isQA = currentUserRoles.includes('Quality Assurance');
  const isAdmin = currentUserRoles.includes('Admin');

  // Only allow Superadmin to always edit all core details, matching backend logic
  const isSuperadmin = currentUserRoles.includes('Superadmin');
  const canEditCoreDetails = isSuperadmin || isManager || isNewClient;
  const canEditFinancials = isNewClient || isManager || isQA;
  const canManageAttachments = isNewClient || isManager || isQA;
  const canUploadContracts = true; // Allow all users to upload contracts
  const canApproveDecline = isManager || isQA;
  const canEditRemarks = isSuperadmin || isManager || isQA;
  const canSchedule = true;
  const canEditFeedbackAndRemarks = isSuperadmin || isManager || isQA || isAdmin;
  const canManageTaskStatus = isManager || isQA || isAdmin;

  const canEditClient = usePermission('Clients', 'ClientList', 'edit');
  const canCreateClient = usePermission('Clients', 'ClientList', 'create');

  // Track if user has made changes to prevent unwanted resets
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const [showConvertPanel, setShowConvertPanel] = useState(false);
  const [convertTarget, setConvertTarget] = useState<'Yearly' | 'Monthly' | null>(null);
  const [convertStartDate, setConvertStartDate] = useState(todayDateInputValue());
  const [convertPrice, setConvertPrice] = useState<number>(0);

  useEffect(() => {
    // Only reset form data when client prop changes and user hasn't made changes
    // Don't reset when errors are cleared
    if (!hasUserChanges) {
      setFormData(client);
    }
  }, [client, hasUserChanges]);

  // Mark that user has made changes when they interact with the form
  const markUserChanges = () => {
    if (!hasUserChanges) {
      setHasUserChanges(true);
    }
  };

  useEffect(() => {
    if ((canEditClient || canCreateClient) && isOpen) {
      // Use different endpoint based on user role
      const endpoint = '/api/users/sales-agents?getAllUsers=true';

      fetch(endpoint, { credentials: 'include' })
        .then(async res => {
          if (!res.ok) return setUsers([]);
          try {
            const data = await res.json();
            // Extract users array from paginated response
            const usersArray = data.users || data;
            setUsers(Array.isArray(usersArray) ? usersArray : []);
          } catch (error) {
            console.error('Error parsing users response:', error);
            setUsers([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching users:', error);
          setUsers([]);
        });
    }
  }, [canEditClient, canCreateClient, isOpen, currentUserRoles]);

  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/accounting/companies?activeOnly=true', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        setAccountingCompanies(Array.isArray(data) ? data : []);
      })
      .catch(() => setAccountingCompanies([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowConvertPanel(false);
      setConvertTarget(null);
    }
  }, [isOpen]);

  const openConvertPanel = (target: 'Yearly' | 'Monthly') => {
    markUserChanges();
    setConvertTarget(target);
    setShowConvertPanel(true);
    setConvertStartDate(todayDateInputValue());

    if (target === 'Monthly') {
      const suggested =
        formData.monthlyPayment ||
        deriveMonthlyFromYearly(formData.totalPrice || 0) ||
        0;
      setConvertPrice(suggested);
    } else {
      const suggested =
        formData.totalPrice ||
        deriveYearlyFromMonthly(formData.monthlyPayment || formData.firstPayment || 0) ||
        0;
      setConvertPrice(suggested);
    }
  };

  const applyBillingConversion = () => {
    if (!convertTarget || !convertStartDate) {
      setLocalError('Please set the billing start date for the conversion.');
      return;
    }
    if (!convertPrice || convertPrice <= 0) {
      setLocalError('Please enter a valid price for the new billing period.');
      return;
    }

    const startIso = new Date(convertStartDate).toISOString();
    markUserChanges();

    if (convertTarget === 'Monthly') {
      setFormData((prev) => ({
        ...prev,
        paymentType: 'Monthly',
        monthlyBillingStartDate: startIso,
        yearlyBillingStartDate: prev.yearlyBillingStartDate || prev.signupDate || startIso,
        firstPayment: convertPrice,
        monthlyPayment: convertPrice,
        billingConvertedAt: new Date().toISOString(),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        paymentType: 'Yearly',
        yearlyBillingStartDate: startIso,
        monthlyBillingStartDate: prev.monthlyBillingStartDate || prev.signupDate || startIso,
        totalPrice: convertPrice,
        billingConvertedAt: new Date().toISOString(),
      }));
    }

    setShowConvertPanel(false);
    setConvertTarget(null);
    setLocalError(null);
  };

  const handleBillingStartDateChange = (
    field: 'yearlyBillingStartDate' | 'monthlyBillingStartDate',
    value: string
  ) => {
    markUserChanges();
    setFormData((prev) => ({
      ...prev,
      [field]: value ? new Date(value).toISOString() : null,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setLocalError(null); // Clear local error when user changes form
    markUserChanges(); // Mark that user has made changes
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }
    const isNumberInput = type === 'number';

    // Special handling for phone number - only allow digits and prevent starting with 0
    if (name === 'phone') {
      // Remove all non-digit characters
      const cleaned = value.replace(/\D/g, '');
      // Don't allow numbers starting with 0
      if (cleaned.startsWith('0')) {
        return;
      }
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }

    // For nested onboardingTasks, handle separately
    if (name.startsWith('onboardingTasks.')) {
      const taskKey = name.split('.')[1] as keyof OnboardingTasks;
      setFormData(prev => ({
        ...prev,
        onboardingTasks: {
          ...prev.onboardingTasks,
          [taskKey]: value,
        },
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: isNumberInput ? parseFloat(value) || 0 : value }));
  };

  const handleRequirementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => {
      const newTasks = { ...prev.onboardingTasks };

      if (name === 'hasWebsite') {
        newTasks.hasWebsite = checked;
        if (!checked) {
          newTasks.websiteDelivered = { status: TaskStatus.Pending, remarks: [], scheduledDate: null };
          newTasks.websiteTrainingCompleted = { status: TaskStatus.Pending, remarks: [], scheduledDate: null };
        }
      }
      if (name === 'hasSalesFunnel') {
        newTasks.hasSalesFunnel = checked;
        if (!checked) {
          newTasks.salesFunnelDelivered = { status: TaskStatus.Pending, remarks: [], scheduledDate: null };
        }
      }
      if (name === 'hasAIWhatsAppBot') {
        newTasks.hasAIWhatsAppBot = checked;
        if (!checked) {
          newTasks.aiWhatsAppBotUsers = undefined;
          newTasks.aiWhatsAppBotDelivered = { status: TaskStatus.Pending, remarks: [], scheduledDate: null };
        }
      }

      return { ...prev, onboardingTasks: newTasks };
    });
  };

  const handleOperatorReportTask = (taskName: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>) => {
    const newStatus = TaskStatus.Reported;
    const remark = {
      id: new Date().toISOString(),
      text: `Task reported as done by ${currentUserRoles.join(', ')}.`,
      authorRole: currentUserRoles.join(', '),
      timestamp: new Date().toISOString(),
      action: 'Reported' as const
    };

    const task = formData.onboardingTasks[taskName];
    if (typeof task !== 'object' || task === null) return;

    setFormData(prev => ({
      ...prev,
      onboardingTasks: {
        ...prev.onboardingTasks,
        [taskName]: {
          ...task,
          status: newStatus,
          remarks: [...task.remarks, remark]
        },
      }
    }));
  };

  const handleScheduleChange = async (e: React.ChangeEvent<HTMLInputElement>, taskKey: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>) => {
    const { value } = e.target;
    const scheduledDate = value ? new Date(value).toISOString() : null;
    const scheduledBy = currentUsername || null;

    setFormData(prev => {
      const existingTask = prev.onboardingTasks[taskKey];
      const updatedTask = {
        ...(typeof existingTask === 'object' && existingTask !== null ? existingTask : {}),
        scheduledDate,
        scheduledBy,
        status: TaskStatus.Pending,
      };

      return {
        ...prev,
        onboardingTasks: {
          ...prev.onboardingTasks,
          [taskKey]: updatedTask,
        },
      };
    });

    // Also create/update the backend OnboardingTask if scheduling
    if (client.id && scheduledDate) {
      // Map taskKey to type for backend
      const TASK_KEY_TO_TYPE: Record<string, string> = {
        adminTrainingCompleted: 'adminTraining',
        agentTrainingCompleted: 'agentTraining',
        websiteTrainingCompleted: 'websiteTraining',
      };
      const type = TASK_KEY_TO_TYPE[taskKey as string];
      if (type) {
        await fetch(`/api/clients/${client.id}/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type,
            status: TaskStatus.Pending,
            scheduledDate,
            scheduledBy,
            remarks: [],
          }),
        });
      }
    }
  };

  const handlePaymentDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      paymentDueDate: value ? new Date(value).toISOString() : null
    }));
  };

  const getLocalDateForInput = (isoDate: string | null | undefined) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().slice(0, 10);
  };

  const handleSuggestRemarks = useCallback(async () => {
    setIsGenerating(true);
    try {
      const suggestion = await generateRemarksSuggestion(formData);
      setFormData(prev => ({ ...prev, qualityCheckRemarks: suggestion }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  }, [formData]);

  const handleAddAttachment = async () => {
    setUploadError(null);
    setLocalError(null); // Clear local error when user adds attachment
    markUserChanges(); // Mark that user has made changes
    if (!newAttachmentFile || !formData.company) {
      setUploadError('Please select a file and ensure company name is filled.');
      return;
    }
    setUploadingAttachment(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', newAttachmentFile);
      formDataObj.append('companyName', formData.company);
      // Use the file's original name
      formDataObj.append('fileName', newAttachmentFile.name);
      // Call backend API to upload file (API to be implemented)
      const res = await fetch('/api/contracts/upload', {
        method: 'POST',
        credentials: 'include',
        body: formDataObj,
      });
      if (!res.ok) {
        const errorData = await res.json();
        setUploadError(errorData.error || 'Failed to upload file');
        return;
      }
      const data = await res.json();
      const newAttachment: FileAttachment = {
        id: Date.now().toString(),
        name: newAttachmentFile.name,
        type: newAttachmentType,
        uploadDate: new Date().toISOString(),
        url: data.url,
      };
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, newAttachment],
      }));
      setNewAttachmentFile(null);
    } catch (error) {
      setUploadError('Failed to upload file');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setLocalError(null); // Clear local error when user removes attachment
    markUserChanges(); // Mark that user has made changes
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== id),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Check if user has selected a file but not uploaded it
    if (newAttachmentFile) {
      const errorMessage = 'Please upload the selected file by clicking "Add" or remove the file selection before saving.';
      console.log('Setting local error:', errorMessage);
      setLocalError(errorMessage);
      return;
    }

    // Validate required fields
    if (!formData.signupDate) {
      setLocalError('Signup Date is required.');
      return;
    }

    if (!formData.salesAgentId) {
      setLocalError('Client Owner (Sales Agent) is required.');
      return;
    }

    // Validate payment fields based on payment type
    const paymentType = formData.paymentType || 'Yearly';
    if (paymentType === 'Yearly') {
      if (!formData.totalPrice || formData.totalPrice <= 0) {
        setLocalError('Total Price is required for yearly payment.');
        return;
      }
    } else if (paymentType === 'Monthly') {
      if (!formData.firstPayment || formData.firstPayment <= 0) {
        setLocalError('First Payment is required for monthly payment.');
        return;
      }
      if (!formData.monthlyPayment || formData.monthlyPayment <= 0) {
        setLocalError('Monthly Payment is required for monthly payment.');
        return;
      }
    }

    // Normalize billing start dates on save
    const payload: Client = { ...formData };
    if (paymentType === 'Yearly') {
      if (!payload.yearlyBillingStartDate && payload.signupDate) {
        payload.yearlyBillingStartDate = payload.signupDate;
      }
    } else {
      if (!payload.monthlyBillingStartDate && payload.signupDate) {
        payload.monthlyBillingStartDate = payload.signupDate;
      }
    }
    if (isNewClient) {
      if (paymentType === 'Yearly') {
        payload.yearlyBillingStartDate = payload.signupDate || payload.yearlyBillingStartDate;
      } else {
        payload.monthlyBillingStartDate = payload.signupDate || payload.monthlyBillingStartDate;
      }
    }

    onSave(payload);
  };

  const activePaymentType = formData.paymentType || 'Yearly';
  const yearlyStartDisplay = getYearlyBillingStart(formData) || formData.signupDate || '';
  const monthlyStartDisplay = getMonthlyBillingStart(formData) || formData.signupDate || '';
  const yearlyPeriodExpected = yearlyStartDisplay ? getYearlyExpectedToDate(formData) : 0;
  const monthlyPeriodExpected = monthlyStartDisplay ? getMonthlyExpectedToDate(formData) : 0;

  const renderTask = (label: string, taskKey: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>, required: boolean) => {
    const task = formData.onboardingTasks[taskKey];
    if (typeof task !== 'object' || task === null) return null; // Type guard

    let statusText;
    switch (task.status) {
      case TaskStatus.Approved:
        statusText = 'Approved';
        break;
      case TaskStatus.Reported:
        statusText = 'Awaiting Approval';
        break;
      case TaskStatus.Pending:
      default:
        statusText = task.scheduledDate
          ? `Scheduled for ${new Date(task.scheduledDate).toLocaleDateString()}`
          : 'Pending';
        break;
    }

    if (!required) return null;

    return (
      <div key={taskKey} className="p-2 bg-slate-50 rounded-md space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-800">{label}</span>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${task.status === TaskStatus.Approved ? 'bg-green-100 text-green-800' :
              task.status === TaskStatus.Reported ? 'bg-yellow-100 text-yellow-800' :
                'bg-slate-200 text-slate-600'
              }`}>{statusText}</span>
            {isOperator && task.status === TaskStatus.Pending && (
              <button type="button" onClick={() => handleOperatorReportTask(taskKey)} className="px-2 py-1 text-xs font-semibold text-white bg-blue-500 rounded hover:bg-blue-600">Report Done</button>
            )}
            {canManageTaskStatus && client.id && (
              <>
                {task.status !== TaskStatus.Pending && (
                  <button type="button" onClick={() => onTaskStatusUpdateRequest(client.id, taskKey, TaskStatus.Pending)} className="px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded hover:bg-red-600">Mark Pending</button>
                )}
                {task.status !== TaskStatus.Approved && (
                  <button type="button" onClick={() => onTaskStatusUpdateRequest(client.id, taskKey, TaskStatus.Approved)} className="px-2 py-1 text-xs font-semibold text-white bg-green-500 rounded hover:bg-green-600">Mark Approved</button>
                )}
              </>
            )}
          </div>
        </div>
        {task.status === TaskStatus.Pending && canSchedule && (
          <div>
            <label htmlFor={`${taskKey}-schedule`} className="sr-only">Schedule Date</label>
            <DatePicker
              selected={task.scheduledDate ? new Date(task.scheduledDate) : null}
              onChange={date => handleScheduleChange({ target: { value: date ? date.toISOString() : '' } } as any, taskKey)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy-MM-dd HH:mm"
              className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-xs"
              placeholderText="Select date & time"
              id={`${taskKey}-schedule`}
            />
          </div>
        )}
      </div>
    );
  };

  const title = isNewClient ? (isSalesAgent ? 'Add New Client' : 'Add New Client') : 'Edit Client Details';

  const handleAnalyzeSatisfaction = async () => {
    setIsAnalyzing(true);
    try {
      // Call your AI service with all client details, comments, and feedback
      const result = await generateSatisfactionAnalysis(formData);
      setFormData(prev => ({
        ...prev,
        satisfactionLevel: result.satisfactionLevel as SatisfactionLevel,
        renewalPrediction: result.renewalPrediction as RenewalPrediction,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Reset user changes flag when modal opens/closes or new client is loaded
  useEffect(() => {
    if (!isOpen) {
      setHasUserChanges(false);
    }
  }, [isOpen]);

  // Reset user changes flag when client changes (new client loaded)
  useEffect(() => {
    setHasUserChanges(false);
  }, [client.id]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="max-w-full w-full max-h-[95vh] overflow-y-auto p-2 sm:p-3 md:p-4 pt-2 sm:pt-3 md:pt-4">
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="mb-3 sm:mb-4 bg-white rounded-lg border border-slate-200 shadow-sm p-2 sm:p-3 md:p-4">
            <h3 className="text-base font-semibold text-slate-700 mb-2 sm:mb-3">Core Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700">Client Name</label>
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-slate-700">Company Name</label>
                <input type="text" name="company" id="company" value={formData.company} onChange={handleChange} required disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
                <p className="mt-1 text-xs text-slate-500">Customer&apos;s business name (shown on contracts and communications)</p>
              </div>
              <div>
                <label htmlFor="companyId" className="block text-sm font-medium text-slate-700">Invoicing Company</label>
                <select
                  name="companyId"
                  id="companyId"
                  value={formData.companyId || 'secondary'}
                  onChange={handleChange}
                  disabled={!isNewClient && !canEditClient}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                >
                  {accountingCompanies.length === 0 ? (
                    <option value="secondary">Secondary (Empire IT)</option>
                  ) : (
                    accountingCompanies.map((co) => (
                      <option key={co.code} value={co.code}>
                        {co.displayName || co.name}
                        {co.accountingMethod === 'accrual' ? ' · Accrual' : ''}
                      </option>
                    ))
                  )}
                </select>
                <p className="mt-1 text-xs text-slate-500">Legal entity for AR invoices and journal entries</p>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">Contact Email</label>
                <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700">Phone Number</label>
                <input type="tel" name="phone" id="phone" value={formData.phone || ''} onChange={handleChange} required disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder="e.g., 971543337687" />
                <p className="mt-1 text-xs text-slate-500">Format: Numbers only, no + or spaces, cannot start with 0 (e.g., 971543337687)</p>
              </div>
              <div>
                <label htmlFor="vatNumber" className="block text-sm font-medium text-slate-700">VAT Number</label>
                <input type="text" name="vatNumber" id="vatNumber" value={formData.vatNumber || ''} onChange={handleChange} disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder="Enter VAT number (optional)" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-slate-700">Address</label>
                <textarea
                  name="address"
                  id="address"
                  value={formData.address || ''}
                  onChange={handleChange}
                  rows={3}
                  disabled={!isNewClient && !canEditClient}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                  placeholder="Enter client address (optional)"
                />
              </div>
              <div>
                <label htmlFor="numberOfUsers" className="block text-sm font-medium text-slate-700">Number of Users</label>
                <input type="number" name="numberOfUsers" id="numberOfUsers" value={formData.numberOfUsers} onChange={handleChange} required min="1" disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-slate-700">Status</label>
                <select
                  name="status"
                  id="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={!isNewClient && !canEditClient}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                >
                  {Object.values(ClientStatus).map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="signupDate" className="block text-sm font-medium text-slate-700">Signup Date *</label>
                <DatePicker
                  selected={formData.signupDate ? new Date(formData.signupDate) : null}
                  onChange={date => handleChange({ target: { name: 'signupDate', value: date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : '' } } as any)}
                  dateFormat="yyyy-MM-dd"
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                  disabled={!isNewClient && !canEditClient}
                  placeholderText="Select signup date"
                  id="signupDate"
                  required
                />
              </div>
              <div>
                <label htmlFor="leadSource" className="block text-sm font-medium text-slate-700">Lead Source</label>
                <input type="text" name="leadSource" id="leadSource" value={formData.leadSource || ''} onChange={handleChange} disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="salesAgentId" className="block text-sm font-medium text-slate-700">Client Owner (Sales Agent) *</label>
                <select
                  name="salesAgentId"
                  id="salesAgentId"
                  value={formData.salesAgentId || ''}
                  onChange={handleChange}
                  disabled={!isNewClient && !canEditClient}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select Sales Agent</option>
                  {(isSalesAgent ? users.filter(user => user.username === currentUsername) : users).map(user => (
                    <option key={user.id} value={user.username}>{user.name} ({user.username})</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="helpingSalesAgent" className="block text-sm font-medium text-slate-700">Helping Sales Agent</label>
                <select name="helpingSalesAgent" id="helpingSalesAgent" value={formData.helpingSalesAgent || ''} onChange={handleChange} disabled={!isNewClient && !canEditClient} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed">
                  <option value="">None</option>
                  {users.filter(user => user.username !== formData.salesAgentId).map(user => (
                    <option key={user.id} value={user.username}>{user.name} ({user.username})</option>
                  ))}
                </select>
              </div>
              {formData.helpingSalesAgent && (
                <div>
                  <label htmlFor="ownerSplitPercent" className="block text-sm font-medium text-slate-700">Owner Split %</label>
                  <select
                    name="ownerSplitPercent"
                    id="ownerSplitPercent"
                    value={formData.ownerSplitPercent || 75}
                    onChange={e => {
                      markUserChanges();
                      setFormData(prev => ({ ...prev, ownerSplitPercent: Number(e.target.value) as 50 | 75 }));
                    }}
                    disabled={!isNewClient && !canEditClient}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
                  >
                    <option value={75}>75% / 25% (Owner / Helper)</option>
                    <option value={50}>50% / 50% (Owner / Helper)</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">Commission split between owner and helping agent</p>
                </div>
              )}
              {!canEditClient && formData.onboardingTasks.hasWebsite && formData.onboardingTasks.websiteType && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700">Website Type</label>
                  <div className="mt-1 px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-700">
                    {formData.onboardingTasks.websiteType}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-slate-100 my-2 sm:my-3"></div>
          <div className="mb-3 sm:mb-4 bg-white rounded-lg border border-slate-200 shadow-sm p-2 sm:p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <h4 className="text-base font-semibold text-slate-700">Sales Information</h4>
              {!isNewClient && (
                <div className="flex gap-2">
                  {activePaymentType === 'Yearly' ? (
                    <button
                      type="button"
                      onClick={() => openConvertPanel('Monthly')}
                      className="px-3 py-1.5 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100"
                    >
                      Convert to Monthly
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openConvertPanel('Yearly')}
                      className="px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100"
                    >
                      Convert to Yearly
                    </button>
                  )}
                </div>
              )}
            </div>

            {showConvertPanel && convertTarget && (
              <div className="mb-4 p-3 rounded-lg border-2 border-dashed border-primary-300 bg-primary-50/50">
                <p className="text-sm font-semibold text-slate-800 mb-2">
                  Convert to {convertTarget} billing
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {convertTarget} billing starts
                    </label>
                    <input
                      type="date"
                      value={convertStartDate}
                      onChange={(e) => setConvertStartDate(e.target.value)}
                      className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {convertTarget === 'Monthly' ? 'Monthly rate (د.إ)' : 'Yearly price (د.إ)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={convertPrice || ''}
                      onChange={(e) => setConvertPrice(parseFloat(e.target.value) || 0)}
                      className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      {convertTarget === 'Monthly'
                        ? formData.totalPrice
                          ? `Suggested: ${deriveMonthlyFromYearly(formData.totalPrice).toLocaleString()} (yearly ÷ 12)`
                          : 'Enter monthly subscription amount'
                        : formData.monthlyPayment
                          ? `Suggested: ${deriveYearlyFromMonthly(formData.monthlyPayment).toLocaleString()} (monthly × 12)`
                          : 'Enter yearly contract amount'}
                    </p>
                  </div>
                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={applyBillingConversion}
                      className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                    >
                      Apply conversion
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowConvertPanel(false); setConvertTarget(null); }}
                      className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className={`p-3 rounded-lg border ${activePaymentType === 'Yearly' ? 'border-teal-300 bg-teal-50/60' : 'border-slate-200 bg-slate-50'}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-2">Yearly period</p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-0.5">Start date</label>
                    <input
                      type="date"
                      value={getLocalDateForInput(yearlyStartDisplay || undefined)}
                      onChange={(e) => handleBillingStartDateChange('yearlyBillingStartDate', e.target.value)}
                      disabled={isNewClient && activePaymentType !== 'Yearly'}
                      className="block w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="totalPrice" className="block text-xs text-slate-600 mb-0.5">Yearly price (د.إ)</label>
                    <input
                      type="number"
                      name="totalPrice"
                      id="totalPrice"
                      value={formData.totalPrice ?? ''}
                      onChange={handleChange}
                      min="0"
                      disabled={activePaymentType !== 'Yearly' && !isNewClient}
                      className="block w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-100"
                    />
                  </div>
                  {yearlyStartDisplay && (formData.totalPrice || 0) > 0 && (
                    <p className="text-[11px] text-slate-500">
                      Expected to date: د.إ{yearlyPeriodExpected.toLocaleString()}
                      {formData.monthlyPayment ? ` · ≈ د.إ${deriveMonthlyFromYearly(formData.totalPrice || 0).toLocaleString()}/mo` : ''}
                    </p>
                  )}
                </div>
              </div>

              <div className={`p-3 rounded-lg border ${activePaymentType === 'Monthly' ? 'border-violet-300 bg-violet-50/60' : 'border-slate-200 bg-slate-50'}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2">Monthly period</p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-0.5">Start date</label>
                    <input
                      type="date"
                      value={getLocalDateForInput(monthlyStartDisplay || undefined)}
                      onChange={(e) => handleBillingStartDateChange('monthlyBillingStartDate', e.target.value)}
                      disabled={isNewClient && activePaymentType !== 'Monthly'}
                      className="block w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-100"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="firstPayment" className="block text-xs text-slate-600 mb-0.5">First (د.إ)</label>
                      <input
                        type="number"
                        name="firstPayment"
                        id="firstPayment"
                        value={formData.firstPayment ?? ''}
                        onChange={handleChange}
                        min="0"
                        disabled={activePaymentType !== 'Monthly' && !isNewClient}
                        className="block w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="monthlyPayment" className="block text-xs text-slate-600 mb-0.5">Monthly (د.إ)</label>
                      <input
                        type="number"
                        name="monthlyPayment"
                        id="monthlyPayment"
                        value={formData.monthlyPayment ?? ''}
                        onChange={handleChange}
                        min="0"
                        disabled={activePaymentType !== 'Monthly' && !isNewClient}
                        className="block w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-100"
                      />
                    </div>
                  </div>
                  {monthlyStartDisplay && (formData.monthlyPayment || formData.firstPayment) && (
                    <p className="text-[11px] text-slate-500">
                      Expected to date: د.إ{monthlyPeriodExpected.toLocaleString()}
                      {formData.totalPrice ? ` · ≈ د.إ${deriveYearlyFromMonthly(formData.monthlyPayment || 0).toLocaleString()}/yr` : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
              <div className="mb-2 sm:mb-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">Active billing type</label>
                {isNewClient ? (
                  <select
                    name="paymentType"
                    value={formData.paymentType || 'Yearly'}
                    onChange={(e) => {
                      markUserChanges();
                      const pt = e.target.value as 'Yearly' | 'Monthly';
                      setFormData((prev) => ({ ...prev, paymentType: pt }));
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="Yearly">Yearly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                ) : (
                  <div className={`mt-1 px-3 py-2 rounded-md text-sm font-semibold inline-flex ${
                    activePaymentType === 'Monthly'
                      ? 'bg-violet-100 text-violet-800'
                      : 'bg-teal-100 text-teal-800'
                  }`}>
                    {activePaymentType} Client
                    {formData.billingConvertedAt && (
                      <span className="ml-2 font-normal text-xs opacity-75">
                        (converted {new Date(formData.billingConvertedAt).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="mb-2 sm:mb-3 flex items-center">
                <input
                  type="checkbox"
                  name="splitPayments"
                  checked={formData.splitPayments || false}
                  onChange={e => {
                    markUserChanges();
                    setFormData(prev => ({ ...prev, splitPayments: e.target.checked }));
                  }}
                  className="h-4 w-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                />
                <label className="ml-2 block text-sm font-medium text-slate-700">Split Payments</label>
              </div>
              {activePaymentType === 'Monthly' && (
                <div className="mb-2 sm:mb-3">
                  <label htmlFor="paymentDueDate" className="block text-sm font-medium text-slate-700">Payment Due Date</label>
                  <input type="date" name="paymentDueDate" id="paymentDueDate" value={getLocalDateForInput(formData.paymentDueDate)} onChange={handlePaymentDueDateChange} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm" />
                </div>
              )}
              {formData.splitPayments && (
                <div className="mb-2 sm:mb-3 md:col-span-2">
                  <label htmlFor="splitPaymentDueDate" className="block text-sm font-medium text-slate-700">Next Payment Date</label>
                  <input
                    type="date"
                    name="splitPaymentDueDate"
                    id="splitPaymentDueDate"
                    value={getLocalDateForInput(formData.paymentDueDate)}
                    onChange={e => {
                      markUserChanges();
                      const value = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        paymentDueDate: value ? new Date(value).toISOString() : null,
                        isPaymentPending: value ? true : false
                      }));
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              )}
            </div>
            {/* Attachments */}
            <div className="border-t border-slate-100 pt-4">
              <h5 className="text-sm font-medium text-slate-700 mb-2">Attachments</h5>
              <ul className="space-y-2 mb-3">
                {formData.attachments.map(att => (
                  <li key={att.id} className="flex items-center justify-between bg-slate-100 p-2 rounded-md text-sm">
                    <span>{att.name} ({att.type}) {att.url && (<a href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-2">View</a>)}</span>
                    {canManageAttachments && (
                      <button type="button" onClick={() => handleRemoveAttachment(att.id)} className="p-1 text-red-500 hover:text-red-700">
                        <XIcon className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {canUploadContracts && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Upload Contract</label>
                  <div className="flex items-stretch gap-2 flex-wrap">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={e => {
                        setLocalError(null); // Clear local error when user selects file
                        setNewAttachmentFile(e.target.files?.[0] || null);
                      }}
                      className="px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    <button type="button" onClick={handleAddAttachment} className="px-4 py-2 bg-slate-600 text-white font-semibold rounded-lg shadow-sm hover:bg-slate-700" disabled={uploadingAttachment || !newAttachmentFile}>{uploadingAttachment ? 'Uploading...' : 'Add'}</button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Upload PDF contracts. Files are stored securely and can be viewed later.</p>
                </div>
              )}
              {uploadError && <div className="text-red-600 text-xs mt-1">{uploadError}</div>}
            </div>
          </div>

          <div className="mb-3 sm:mb-4 bg-white rounded-lg border border-slate-200 shadow-sm p-2 sm:p-3 md:p-4">
            <h4 className="text-base font-semibold text-slate-700 mb-2 sm:mb-3">Onboarding Progress</h4>
            <div className="space-y-2">
              {/* Requirements */}
              <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 sm:gap-x-6 gap-y-2">
                <legend className="text-sm font-medium text-slate-700 col-span-full mb-1">Service Requirements</legend>
                <div className="flex items-center">
                  <input id="hasWebsite" name="hasWebsite" type="checkbox" checked={formData.onboardingTasks.hasWebsite} onChange={handleRequirementChange} className="h-4 w-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500 disabled:bg-slate-200" disabled={!isNewClient && !canEditClient} />
                  <label htmlFor="hasWebsite" className="ml-2 block text-sm text-slate-900">Requires Website</label>
                </div>
                <div className="flex items-center">
                  <input id="hasSalesFunnel" name="hasSalesFunnel" type="checkbox" checked={formData.onboardingTasks.hasSalesFunnel} onChange={handleRequirementChange} className="h-4 w-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500 disabled:bg-slate-200" disabled={!isNewClient && !canEditClient} />
                  <label htmlFor="hasSalesFunnel" className="ml-2 block text-sm text-slate-900">Requires Sales Funnel</label>
                </div>
                <div className="flex items-center">
                  <input id="hasAIWhatsAppBot" name="hasAIWhatsAppBot" type="checkbox" checked={formData.onboardingTasks.hasAIWhatsAppBot || false} onChange={handleRequirementChange} className="h-4 w-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500 disabled:bg-slate-200" disabled={!isNewClient && !canEditClient} />
                  <label htmlFor="hasAIWhatsAppBot" className="ml-2 block text-sm text-slate-900">AI WhatsApp Bot</label>
                </div>
                {/* AI WhatsApp Bot Users Input */}
                {formData.onboardingTasks.hasAIWhatsAppBot && (
                  <div className="col-span-1 sm:col-span-2 flex items-center mt-2">
                    <label htmlFor="aiWhatsAppBotUsers" className="block text-sm font-medium text-slate-700 mr-2">Number of Bot Users</label>
                    <input
                      type="number"
                      id="aiWhatsAppBotUsers"
                      name="aiWhatsAppBotUsers"
                      min="1"
                      value={formData.onboardingTasks.aiWhatsAppBotUsers || ''}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        onboardingTasks: {
                          ...prev.onboardingTasks,
                          aiWhatsAppBotUsers: e.target.value ? parseInt(e.target.value, 10) : undefined
                        }
                      }))}
                      className="block w-32 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      disabled={!isNewClient && !canEditClient}
                      placeholder="e.g., 5"
                    />
                  </div>
                )}
                {/* Website Type Dropdown */}
                {formData.onboardingTasks.hasWebsite && (
                  <div className="col-span-1 sm:col-span-2 flex items-center mt-2">
                    <label htmlFor="websiteType" className="block text-sm font-medium text-slate-700 mr-2">Website Type</label>
                    <select
                      id="websiteType"
                      name="websiteType"
                      value={formData.onboardingTasks.websiteType || ''}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        onboardingTasks: {
                          ...prev.onboardingTasks,
                          websiteType: e.target.value
                        }
                      }))}
                      className="mt-1 block px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      disabled={!isNewClient && !canEditClient}
                    >
                      <option value="">Select Website Type</option>
                      <option value="REM WEB">REM WEB</option>
                      <option value="Wordpress Web">Wordpress Web</option>
                    </select>
                  </div>
                )}
              </fieldset>
              <div className="border-t border-slate-100"></div>
              {/* Task Statuses */}
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-slate-700">Task Status & Scheduling</h5>
                {renderTask('Website Delivery', 'websiteDelivered', formData.onboardingTasks.hasWebsite)}
                {renderTask('Sales Funnel Delivery', 'salesFunnelDelivered', formData.onboardingTasks.hasSalesFunnel)}
                {renderTask('Admin Training', 'adminTrainingCompleted', true)}
                {renderTask('Agent Training', 'agentTrainingCompleted', true)}
                {renderTask('Website Training', 'websiteTrainingCompleted', formData.onboardingTasks.hasWebsite)}
              </div>
            </div>
          </div>
          <div className="mb-3 sm:mb-4 bg-white rounded-lg border border-slate-200 shadow-sm p-2 sm:p-3 md:p-4">
            <h4 className="text-base font-semibold text-slate-700 mb-2 sm:mb-3">Client Feedback & Quality Check</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
              <div>
                <label htmlFor="clientFeedback" className="block text-sm font-medium text-slate-700">Client Feedback</label>
                <textarea name="clientFeedback" id="clientFeedback" rows={3} value={formData.clientFeedback} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm" placeholder="Log any direct feedback or comments from the client here..."></textarea>
              </div>
              <div>
                <label htmlFor="qualityCheckRemarks" className="block text-sm font-medium text-slate-700">Internal Quality Check Remarks</label>
                <div className="mt-1 relative">
                  <textarea name="qualityCheckRemarks" id="qualityCheckRemarks" rows={4} value={formData.qualityCheckRemarks} onChange={handleChange} disabled={!isNewClient && !canEditClient} className="block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-slate-100 disabled:cursor-not-allowed" placeholder="Internal notes for the next quality check call..."></textarea>
                  <button type="button" onClick={handleSuggestRemarks} disabled={isGenerating || (!isNewClient && !canEditClient)} className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    <SparklesIcon className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                    {isGenerating ? 'Generating...' : 'Suggest'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {(isManager || isQA || isAdmin) && (
            <div className="mb-3 sm:mb-4 bg-white rounded-lg border border-slate-200 shadow-sm p-2 sm:p-3 md:p-4">
              <h4 className="text-base font-semibold text-slate-700 mb-2 sm:mb-3">AI Satisfaction Analysis</h4>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-violet-600 text-white font-semibold rounded-lg shadow-sm hover:bg-violet-700 flex items-center gap-2"
                  onClick={handleAnalyzeSatisfaction}
                  disabled={isAnalyzing}
                >
                  <BrainCircuitIcon className="w-5 h-5" />
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Satisfaction'}
                </button>
                <div>
                  <div className="text-sm text-slate-600">Satisfaction Level</div>
                  {formData.satisfactionLevel && (
                    <div className="flex items-center gap-2 mt-2">
                      {(() => {
                        let Icon = MehIcon;
                        let color = 'text-slate-500';
                        switch (formData.satisfactionLevel) {
                          case SatisfactionLevel.VerySatisfied:
                          case SatisfactionLevel.Satisfied:
                            Icon = SmileIcon;
                            color = 'text-green-500';
                            break;
                          case SatisfactionLevel.Neutral:
                            Icon = MehIcon;
                            color = 'text-amber-500';
                            break;
                          case SatisfactionLevel.Dissatisfied:
                          case SatisfactionLevel.VeryDissatisfied:
                            Icon = FrownIcon;
                            color = 'text-red-500';
                            break;
                        }
                        return <><Icon className={`w-5 h-5 ${color}`} /><span className={`font-medium ${color}`}>{formData.satisfactionLevel}</span></>;
                      })()}
                    </div>
                  )}
                  <div className="text-sm text-slate-600 mt-2">Renewal Prediction</div>
                  <div className="font-semibold">{formData.renewalPrediction || 'Not analyzed'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Error display near save button */}
          {(error || localError) && (() => {
            console.log('Rendering error display with:', { error, localError });
            return (
              <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-red-50 border-2 border-red-200 text-red-800 rounded-lg shadow-sm">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium">
                      {error || localError}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="pt-2 sm:pt-3 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
            <button type="button" onClick={onClose} className="px-3 sm:px-4 py-2 bg-white border border-slate-300 text-slate-700 font-semibold rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400">Cancel</button>
            <button type="submit" className="px-3 sm:px-4 py-2 bg-primary-500 text-white font-semibold rounded-lg shadow-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">{isNewClient ? 'Save' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ClientDetailsModal;
